import { useState } from "react";
import { toast } from "sonner";
import { Upload, FileDown, FileText, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { leadsApi, API } from "@/lib/crmApi";

const CSV_COLUMNS = [
  "Date de réception",
  "Nom complet",
  "Téléphone",
  "Email",
  "Ville",
  "Pays",
  "Statut prospect",
  "Horizon acquisition",
  "Source (ad set)",
  "Statut relance",
  "1er contact WhatsApp",
  "Notes",
];

/**
 * Parse a CSV text into list of dicts using the first row as headers.
 * Supports both comma and semicolon separators.
 * Strips BOM if present.
 */
const parseCSV = (text) => {
  const cleaned = text.replace(/^\uFEFF/, "");
  const lines = cleaned.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const sep = lines[0].includes(";") && !lines[0].includes(",") ? ";" : ",";

  // Properly handle quoted fields with embedded commas
  const splitCsvLine = (line) => {
    const out = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (c === sep && !inQuotes) {
        out.push(cur.trim());
        cur = "";
      } else {
        cur += c;
      }
    }
    out.push(cur.trim());
    return out;
  };

  const headers = splitCsvLine(lines[0]).map((h) =>
    h.replace(/^"|"$/g, "").trim()
  );
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = splitCsvLine(lines[i]).map((c) =>
      c.replace(/^"|"$/g, "").trim()
    );
    const row = {};
    headers.forEach((h, idx) => {
      row[h] = cols[idx] || "";
    });
    rows.push(row);
  }
  return rows;
};

const downloadFromUrl = async (url, filename) => {
  // Need credentials for cookie-based auth
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
};

export default function ImportDialog({ open, onOpenChange, onImported }) {
  const [text, setText] = useState("");
  const [skipDup, setSkipDup] = useState(true);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState([]);
  const [report, setReport] = useState(null);
  // For Excel files we keep parsed rows here (since text-area is CSV-only)
  const [excelRows, setExcelRows] = useState(null);
  const [fileName, setFileName] = useState("");

  const parseExcelFile = async (file) => {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const sheetName = wb.SheetNames[0];
    if (!sheetName) return [];
    const ws = wb.Sheets[sheetName];
    // Use header row to produce array of dicts; defval ensures empty cells become ""
    const rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
    // Trim keys & values
    return rows.map((r) => {
      const out = {};
      Object.entries(r).forEach(([k, v]) => {
        out[String(k).trim()] = typeof v === "string" ? v.trim() : v;
      });
      return out;
    });
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setReport(null);
    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    try {
      if (isExcel) {
        const rows = await parseExcelFile(file);
        if (rows.length === 0) {
          toast.error("Fichier Excel vide ou illisible");
          return;
        }
        setExcelRows(rows);
        setText(""); // clear CSV state
        setPreview(rows.slice(0, 3));
      } else {
        const txt = await file.text();
        setExcelRows(null);
        setText(txt);
        setPreview(parseCSV(txt).slice(0, 3));
      }
    } catch (err) {
      toast.error("Erreur lors de la lecture du fichier");
    }
  };

  const handleImport = async () => {
    // Use Excel rows if present, otherwise parse CSV text
    const rows = excelRows && excelRows.length > 0 ? excelRows : parseCSV(text);
    if (rows.length === 0) {
      toast.error("Aucune ligne valide détectée");
      return;
    }
    setLoading(true);
    setReport(null);
    try {
      const res = await leadsApi.importLeads(rows, skipDup);
      setReport({ ...res, total_parsed: rows.length });
      const dup = res.skipped_duplicates ?? res.skipped ?? 0;
      const errs = res.errors?.length || 0;
      if (res.inserted > 0 && dup === 0 && errs === 0) {
        toast.success(`✅ ${res.inserted} lead(s) importé(s) avec succès`);
      } else if (res.inserted > 0) {
        toast.success(
          `✅ ${res.inserted} ajouté(s)${dup ? ` · ${dup} doublon(s)` : ""}${errs ? ` · ${errs} erreur(s)` : ""}`
        );
      } else if (dup > 0 && errs === 0) {
        toast.warning(
          `⚠️ Aucun lead ajouté — ${dup} sont déjà présents (doublons par téléphone). Décochez "Ignorer les doublons" pour forcer l'import.`
        );
      } else {
        toast.error(`Aucun lead ajouté · ${dup} doublons · ${errs} erreurs`);
      }
      onImported?.();
    } catch (e) {
      toast.error("Erreur lors de l'import");
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      await downloadFromUrl(
        `${API}/leads/template.csv`,
        "sogi-leads-template.csv"
      );
      toast.success("Template téléchargé");
    } catch (e) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto"
        data-testid="import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Importer des leads (CSV ou Excel)
          </DialogTitle>
          <DialogDescription>
            Format SOGI · 12 colonnes. Formats acceptés :{" "}
            <span className="font-semibold">.csv</span>,{" "}
            <span className="font-semibold">.xlsx</span>,{" "}
            <span className="font-semibold">.xls</span>. Téléchargez le template pour respecter l'ordre exact.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Header columns reference */}
          <div className="rounded-lg border border-border/60 bg-muted/40 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-heading text-xs font-semibold uppercase tracking-wider">
                Colonnes attendues (dans cet ordre)
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={downloadTemplate}
                data-testid="download-template-btn"
                className="gap-1.5 h-7"
              >
                <FileDown className="h-3 w-3" />
                Template
              </Button>
            </div>
            <ol className="grid grid-cols-2 gap-1 text-[11px] text-muted-foreground md:grid-cols-3">
              {CSV_COLUMNS.map((c, i) => (
                <li key={c}>
                  <span className="font-semibold text-foreground">{i + 1}.</span>{" "}
                  {c}
                </li>
              ))}
            </ol>
          </div>

          {/* File upload */}
          <div className="space-y-1.5">
            <Label htmlFor="csv-file">
              Fichier{" "}
              <span className="text-xs font-normal text-muted-foreground">
                (CSV, XLSX ou XLS)
              </span>
            </Label>
            <Input
              id="csv-file"
              type="file"
              accept=".csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={onFile}
              data-testid="csv-file-input"
            />
            {fileName && excelRows && (
              <div className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300">
                <FileSpreadsheet className="h-3.5 w-3.5" />
                <span>
                  Excel détecté : <span className="font-semibold">{fileName}</span> · {excelRows.length} ligne(s)
                </span>
              </div>
            )}
          </div>

          {/* OR paste text (CSV only) */}
          <div className="space-y-1.5">
            <Label htmlFor="csv-text">Ou collez le contenu CSV</Label>
            <Textarea
              id="csv-text"
              rows={6}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setExcelRows(null);
                setFileName("");
                setPreview(parseCSV(e.target.value).slice(0, 3));
              }}
              placeholder="Date de reception,Nom complet,Telephone,..."
              className="font-mono text-xs"
              data-testid="csv-text-input"
            />
          </div>

          {preview.length > 0 && (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs dark:border-emerald-900/40 dark:bg-emerald-950/20"
              data-testid="csv-preview"
            >
              <div className="font-semibold text-emerald-800 dark:text-emerald-300">
                {preview.length} ligne(s) prévisualisée(s) — premières détectées :
              </div>
              <ul className="mt-1 space-y-0.5">
                {preview.map((r, i) => (
                  <li key={i} className="truncate text-emerald-900 dark:text-emerald-200">
                    {r["Nom complet"] || r["nom"] || "(sans nom)"} —{" "}
                    {r["Téléphone"] || r["Telephone"] || r["telephone"] || "(sans tel)"}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={skipDup}
              onCheckedChange={(v) => setSkipDup(!!v)}
              data-testid="skip-duplicates-checkbox"
            />
            Ignorer les doublons (téléphone déjà existant)
          </label>

          {report && (
            <div
              className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs"
              data-testid="import-report"
            >
              <div className="font-heading mb-2 text-sm font-semibold">
                Rapport d'import
              </div>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                <div className="rounded bg-background p-2">
                  <div className="text-muted-foreground">Reçus</div>
                  <div className="font-heading text-base font-semibold">
                    {report.total_parsed}
                  </div>
                </div>
                <div className="rounded bg-emerald-50 p-2 dark:bg-emerald-950/20">
                  <div className="text-emerald-700 dark:text-emerald-300">
                    Ajoutés
                  </div>
                  <div className="font-heading text-base font-semibold text-emerald-700 dark:text-emerald-300">
                    {report.inserted}
                  </div>
                </div>
                <div className="rounded bg-amber-50 p-2 dark:bg-amber-950/20">
                  <div className="text-amber-700 dark:text-amber-300">
                    Doublons
                  </div>
                  <div className="font-heading text-base font-semibold text-amber-700 dark:text-amber-300">
                    {report.skipped_duplicates ?? report.skipped ?? 0}
                  </div>
                </div>
                <div className="rounded bg-red-50 p-2 dark:bg-red-950/20">
                  <div className="text-red-700 dark:text-red-300">Erreurs</div>
                  <div className="font-heading text-base font-semibold text-red-700 dark:text-red-300">
                    {report.errors?.length || 0}
                  </div>
                </div>
              </div>

              {(report.skipped_duplicates ?? 0) > 0 && (
                <div className="mt-3 rounded border border-amber-200 bg-amber-50/60 p-2 dark:border-amber-900/40 dark:bg-amber-950/10">
                  <div className="font-semibold text-amber-800 dark:text-amber-200">
                    💡 Doublons détectés (téléphones déjà dans la base)
                  </div>
                  {report.duplicate_samples?.length > 0 && (
                    <ul className="mt-1 list-disc pl-4 text-amber-900 dark:text-amber-200">
                      {report.duplicate_samples.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                      {report.skipped_duplicates >
                        report.duplicate_samples.length && (
                        <li className="italic">
                          ... et{" "}
                          {report.skipped_duplicates -
                            report.duplicate_samples.length}{" "}
                          autre(s)
                        </li>
                      )}
                    </ul>
                  )}
                  <div className="mt-1 text-amber-800 dark:text-amber-200">
                    Pour les ré-importer, décochez « Ignorer les doublons » et
                    relancez.
                  </div>
                </div>
              )}

              {report.errors?.length > 0 && (
                <div className="mt-3 rounded border border-red-200 bg-red-50/60 p-2 dark:border-red-900/40 dark:bg-red-950/10">
                  <div className="font-semibold text-red-800 dark:text-red-200">
                    Lignes en erreur (5 premières)
                  </div>
                  <ul className="mt-1 list-disc pl-4 text-red-900 dark:text-red-200">
                    {report.errors.slice(0, 5).map((e, i) => (
                      <li key={i}>
                        Ligne {e.row} : {e.error}
                        {e.nom ? ` — ${e.nom}` : ""}
                        {e.phone ? ` — ${e.phone}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setText("");
              setExcelRows(null);
              setFileName("");
              setPreview([]);
              setReport(null);
              onOpenChange(false);
            }}
          >
            {report ? "Fermer" : "Annuler"}
          </Button>
          <Button
            onClick={handleImport}
            disabled={
              loading ||
              (!text.trim() && (!excelRows || excelRows.length === 0))
            }
            data-testid="csv-import-submit"
            className="gap-1.5 bg-[hsl(var(--accent-navy))] text-white hover:bg-[hsl(var(--accent-navy))]/90"
          >
            <FileText className="h-4 w-4" />
            {loading ? "Import en cours..." : report ? "Réimporter" : "Importer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
